import React from 'react'
import styled, {useTheme} from 'styled-components'
import {Box, Heading, Image, Text, Link} from '@miro-site/core'

import {IHighlighted, IHighlightedItem} from '../api/interfaces'

const Container = styled(Box).attrs(({theme}) => ({
  $py: theme.header.highlighted.verticalPadding,
  $px: theme.header.highlighted.horizontalPadding,
  $styles: {
    borderRadius: theme.header.highlighted.radius,
    backgroundColor: theme.header.highlighted.background,
  },
}))``

const ItemContainer = styled(Box).attrs(({theme}) => ({
  $styles: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))``

const ItemLink = styled(Link)`
  display: block;

  &:focus {
    ${props => props.theme.normalizeStyles(props.theme.focus.outlines.withOffset)}
  }
`

const Headline = styled(Heading).attrs(({theme}) => ({
  as: theme.header.highlighted.headline.as,
  $mb: theme.header.highlighted.margins.afterHeadline,
  $variant: theme.header.highlighted.headline.variant,
}))``

const ViewAllLink = styled(Link).attrs(({theme}) => ({
  $variant: theme.header.highlighted.viewAllLink.variant,
  $color: theme.header.highlighted.viewAllLink.color,
  $size: theme.header.highlighted.viewAllLink.size,
  withArrow: theme.header.highlighted.viewAllLink.withArrow,
}))``

const ItemTitle = styled(Text).attrs(({theme}) => ({
  $mb: theme.header.highlighted.margins.afterTitle,
  $variant: theme.header.highlighted.itemTitle.variant,
}))``

const ItemDescription = styled(Text).attrs(({theme}) => ({
  $variant: theme.header.highlighted.itemDescription.variant,
  $styles: {color: theme.header.highlighted.itemDescription.color},
}))``

const Item = (props: IHighlightedItem): React.ReactElement => {
  const {image, title, description, path} = props

  const theme = useTheme()

  return (
    <ItemLink href={path} $mb={theme.header.highlighted.margins.afterItem}>
      <ItemContainer>
        <Box
          $mr={theme.header.highlighted.margins.afterImage}
          $styles={{width: theme.header.highlighted.imageWidth}}
        >
          {image && <Image aria-hidden={true} {...image} />}
        </Box>
        <Box $styles={{width: theme.header.highlighted.textWidth}}>
          <ItemTitle>{title}</ItemTitle>
          <ItemDescription>{description}</ItemDescription>
        </Box>
      </ItemContainer>
    </ItemLink>
  )
}

const Highlighted = (props: IHighlighted): React.ReactElement => {
  const {title, items, showAllLink} = props
  return (
    <Container>
      <Headline>{title}</Headline>
      <ul>
        {items.map(item => (
          <li key={item.title}>
            <Item {...item} />
          </li>
        ))}
      </ul>
      <ViewAllLink href={showAllLink.path}>{showAllLink.title}</ViewAllLink>
    </Container>
  )
}

export default Object.assign(Highlighted, {Item: Item})
